import React from 'react';
import { get } from 'lodash';
import { TableWidget as ITableWidget } from '../../dashboard-widget-setting/__generated__/TableWidget';
import { WidgetContainer, WidgetContent } from '../WidgetContainer';
import { WidgetHeader } from '../WidgetHeader';
import { ActivityApplicationTableWidget } from './activities-table-widget/activity-application-table-widget/ActivityApplicationTableWidget';
import { ActivityWaitlistingTableWidget } from './activities-table-widget/activity-waitlisting-table-widget/ActivityWaitlistingTableWidget';
import { PrioritisedActivityTableWidget } from './activities-table-widget/prioritised-activities-table-widget/PrioritisedActivityTableWidget';
import { ProgramApplicationsTableWidget } from './programs-table-widget/program-applications-table-widget/ProgramApplicationsTableWidget';
import { VolunteerApplicationsTableWidget } from './volunteers/volunteer-applications-table-widget/VolunteerApplicationsTableWidget';
import { IncidentsTableWidget } from './incidents-table-widget/IncidentsTableWidget';
import { VolunteersAwaitingApprovalTableWidget } from './volunteers/volunteers_awaiting_approval-table-widget/VolunteersAwaitingApprovalTableWidget';
import { CancelledRostersTableWidget } from './rosters-table-widget/cancelled-rosters-table-widget/CancelledRostersTableWidget';
import { valueMap } from '../../dashboard-widget-setting/dashboard-widget-model';
import { VolunteerBirthdaysTableWidget } from './birthdays-table-widget/VolunteerBirthdaysTableWidget';
import { VolunteerAnniversaryTableWidget } from './anniversaries-table-widget/VolunteerAnniversaryTableWidget';
import { TasksRestTableWidget } from './tasks-table-widget/TasksRestTableWidget';
import { CheckedInVolunteersTableWidget } from './checked-in-table-widget/CheckedInVolunteersTableWidget';
import { KeyLinksTableWidget } from './key-links-table-widget/KeyLinksTableWidget';

type TableWidgetProps = {
  widget: ITableWidget;
};

function getWidgetFromTagAndName(tag: string, name: string, filters: string, range: string | null) {
  switch (tag.toLowerCase()) {
    case 'volunteers':
      switch (name) {
        case 'incoming':
          return <VolunteerApplicationsTableWidget filters={filters} range={range} />;
        case 'awaiting':
          return <VolunteersAwaitingApprovalTableWidget filters={filters} range={range} />;
        case 'birthdays':
          return <VolunteerBirthdaysTableWidget filters={filters} />;
        case 'anniversaries':
          return <VolunteerAnniversaryTableWidget filters={filters} />;
        case 'checked-in':
          return <CheckedInVolunteersTableWidget filters={filters} />;
        default:
          return null;
      }
    case 'activities':
      switch (name) {
        case 'application':
          return <ActivityApplicationTableWidget filters={filters} range={range} />;
        case 'waitlist':
          return <ActivityWaitlistingTableWidget filters={filters} range={range} />;
        case 'priority':
          return <PrioritisedActivityTableWidget filters={filters} />;
        default:
          return null;
      }
    case 'incidents':
      return <IncidentsTableWidget filters={filters} status={name} range={range} />;

    case 'tasks':
      switch (name) {
        case 'awaiting':
          // Use REST endpoint for awaiting tasks
          return <TasksRestTableWidget filters={filters} range={range} target='awaiting' />;
        case 'completed':
          return <TasksRestTableWidget filters={filters} range={range} target='completed' />;
        case 'incomplete':
          return <TasksRestTableWidget filters={filters} range={range} target='incomplete' />;
        case 'expiring':
          // Use REST endpoint with expiry date column
          return <TasksRestTableWidget filters={filters} range={range} target='expiring' showExpiryDate />;
        default:
          return null;
      }

    case 'programs':
      switch (name) {
        case 'application':
          return <ProgramApplicationsTableWidget filters={filters} range={range} />;
        default:
          return null;
      }
    case 'rosters':
      switch (name) {
        case 'cancellation':
          return <CancelledRostersTableWidget filters={filters} range={range} />;
        default:
          return null;
      }
    case 'links':
      return name === 'key' ? <KeyLinksTableWidget filters={filters} /> : null;
    default:
      return null;
  }
}

export function TableWidget({ widget }: TableWidgetProps) {
  const { filters, targets, chartOrder, range } = widget;

  const title = React.useMemo(() => {
    const parsedTargets = JSON.parse(targets);
    return parsedTargets
      .map(({ tag, name }: { tag: string; name: string }) =>
        get(valueMap, [tag.toLowerCase(), name.toLowerCase(), 'displayName'], '')
      )
      .join(' & ');
  }, [targets]);

  const { tag, name } = JSON.parse(targets)[0];

  return (
    <WidgetContainer>
      <WidgetHeader title={title} subTitle={chartOrder} />
      <WidgetContent>{getWidgetFromTagAndName(tag, name, filters, range)}</WidgetContent>
    </WidgetContainer>
  );
}
